import {
  CameraRelativeToWristInTooltipCoordinates,
  ZERO_POSE,
} from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type {
  CameraIntegration,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import { makeNamespacedLog } from '@sb/log';
import type { EquipmentInterface } from '@sb/routine-runner';

import type { LocateMatch, LocateMethod } from './LocateTypes';

const log = makeNamespacedLog('vision.helpers');

export function applyDisableRotation(matches: LocateMatch[]): LocateMatch[] {
  return matches.map((match) => ({
    ...match,
    detection: { ...match.detection, rotation: 0 },
  }));
}

export async function getCameraColorFrame(
  cameraEquipment: Camera | WristCamera | undefined,
  cameraIntegration: CameraIntegration | undefined,
): Promise<ArrayBuffer> {
  if (cameraEquipment?.kind === 'WristCamera' && cameraIntegration) {
    return (cameraEquipment as WristCamera).getColorFrame(cameraIntegration);
  }

  if (cameraEquipment?.kind === 'Camera') {
    return (cameraEquipment as Camera).getColorFrame();
  }

  throw new Error('Missing camera equipment or camera integration');
}

/**
 * Resolve the camera-to-robot extrinsics for a vision request. The built-in
 * wrist camera always uses the known physical camera↔flange relationship
 * (tcp); external cameras use their calibrated extrinsics (or the provided
 * override), falling back to identity (base-relative) when uncalibrated.
 */
export async function resolveVisionExtrinsics(
  equipment: Camera | WristCamera,
  extrinsicsOverride?: ExtrinsicsKinematics,
): Promise<ExtrinsicsKinematics> {
  // If the camera is the built-in wrist camera, we always use the known
  // physical relationship between the camera and the flange (tcp)
  if (equipment.kind === 'WristCamera') {
    return {
      extrinsics: CameraRelativeToWristInTooltipCoordinates,
      kind: 'tcp',
    };
  }

  let out = extrinsicsOverride;

  if (!out) {
    const config = equipment.getConfig() as CameraConfiguration;

    const outRaw = equipment.getExtrinsics(true);

    if (outRaw == null) {
      // No calibrated extrinsics — default to identity (base-relative)
      log.warn(
        'resolveVisionExtrinsics',
        'No extrinsics found for camera, defaulting to identity (base)',
        { config },
      );

      out = { extrinsics: ZERO_POSE, kind: 'base' };
    } else {
      out = outRaw;
    }
  } else {
    log.info('resolveVisionExtrinsics', 'Using extrinsics from args', {
      extrinsics: out,
    });
  }

  return out;
}

/**
 * Resolve the GPU Task Runner the locate method's own configuration selects,
 * or undefined for the local (CPU) vision path.
 *
 * An empty/absent `gpuTaskRunnerId` is the explicit "None (CPU only)"
 * selection — honor it verbatim. Do NOT fall back to the first healthy GPU
 * box: that silently reroutes plane projection (and Detection3D inference) to
 * a device the user never selected, which both ignores their choice and
 * breaks when that box runs a different software version than the local
 * vision-bot. A configured-but-missing/unhealthy device logs a warning and
 * falls back to local vision.
 */
export function resolveLocateGpuTaskRunner(
  equipment: EquipmentInterface,
  method: LocateMethod,
  logNamespace: string,
): GPUTaskRunner | undefined {
  let selectedId = '';

  switch (method.kind) {
    case 'TextPromptDetection2D':
    case 'CustomCheckpointDetection2D':
    case 'DinoFast':
    case 'Detection3D':
      selectedId = method.settings.gpuTaskRunnerId;
      break;
    default:
      // Method kinds without a GPU selector always run on local vision.
      break;
  }

  if (!selectedId) {
    return undefined;
  }

  const device = equipment.getDeviceById(selectedId) as
    | GPUTaskRunner
    | undefined;

  if (!device) {
    log.warn(
      logNamespace,
      `GPU Task Runner '${selectedId}' not found, falling back to local vision`,
    );

    return undefined;
  }

  if (!device.update().isVisionBotConnected) {
    log.warn(
      logNamespace,
      `GPU Task Runner '${selectedId}' is not healthy, falling back to local vision`,
    );

    return undefined;
  }

  return device;
}
